import { useState, useEffect, useMemo } from 'react';
import { AlertDialog, AlertDialogContent } from '@/components/ui/alert-dialog';
import { Button } from '@/components/Button';
import Select from '@/components/Select';
import { TextField } from '@/components/TextField';
import { Textarea } from '@/components/Textarea';
import MultiSelect from '../../candidates/components/MultiSelect';
import DatePickerModal from '@/components/DatePickerModal';
import useFetchCompaniesCustomFields from '../api/useFetchCompaniesCustomFields';
import useBulkUpdateCompanyCustomFields from '../api/useBulkUpdateCompanyCustomFields';
import { showSuccessToast, showErrorToast } from '../../job_change_processes/lib/toast';

type Props = {
  ids: number[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
};

export default function CompanyBulkUpdateCustomFieldsModal({
  ids,
  open,
  onOpenChange,
  onSuccess,
}: Props) {
  const [selectedFieldId, setSelectedFieldId] = useState<string>('');
  const [fieldValue, setFieldValue] = useState<string>('');
  const { data: customFields } = useFetchCompaniesCustomFields();
  const { mutateAsync: bulkUpdate, isPending } = useBulkUpdateCompanyCustomFields();

  // モーダルが開かれた時に状態をリセット
  useEffect(() => {
    if (open) {
      setSelectedFieldId('');
      setFieldValue('');
    }
  }, [open]);

  const fieldItems =
    customFields?.records.map((field) => ({
      value: String(field.id),
      label: field.name,
    })) ?? [];

  const selectedField = useMemo(() => {
    if (!selectedFieldId || !customFields?.records) return null;
    return customFields.records.find((field) => field.id === Number(selectedFieldId));
  }, [selectedFieldId, customFields?.records]);

  const handleSubmit = async () => {
    if (!selectedFieldId || !fieldValue) return;
    try {
      await bulkUpdate({
        ids,
        custom_data: [
          {
            field_id: Number(selectedFieldId),
            content: fieldValue,
          },
        ],
      });
      showSuccessToast('企業データを更新しました');
      onSuccess();
    } catch {
      showErrorToast('企業データの更新に失敗しました');
    }
  };

  const handleClose = () => {
    setSelectedFieldId('');
    setFieldValue('');
    onOpenChange(false);
  };

  const canSubmit = selectedFieldId && fieldValue && !isPending;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="w-[640px]! max-w-screen-sm!">
        {/* ヘッダー */}
        <div className="border-b border-solid border-border px-6 py-4">
          <p className="text-heading-xs font-bold">企業データの更新</p>
        </div>

        {/* コンテンツ */}
        <div className="flex flex-col gap-4 p-6">
          {/* 項目選択 */}
          <div className="flex flex-col gap-2">
            <label className="text-heading-xxs font-bold">変更する項目を選択</label>
            <Select
              items={fieldItems}
              defaultLabel="テキスト"
              value={selectedFieldId}
              onValueChange={(value) => {
                setSelectedFieldId(value);
                setFieldValue(''); // 項目変更時に値をリセット
              }}
            />
          </div>

          {/* 値入力 */}
          {selectedFieldId && (
            <div className="flex flex-col gap-2">
              <label className="text-heading-xxs font-bold">変更後の値を入力</label>
              {selectedField?.field_type === 'long_text' ? (
                <Textarea
                  rows={4}
                  placeholder="テキスト"
                  value={fieldValue}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setFieldValue(e.target.value)
                  }
                  className="p-3"
                />
              ) : selectedField?.field_type === 'date' ? (
                <DatePickerModal
                  type="date"
                  value={fieldValue}
                  onChange={(value) => setFieldValue(value)}
                />
              ) : selectedField?.field_type === 'datetime' ? (
                <DatePickerModal
                  type="datetime"
                  value={fieldValue}
                  onChange={(value) => setFieldValue(value)}
                />
              ) : selectedField?.field_type === 'single_select' ? (
                <Select
                  items={
                    selectedField.custom_field_options?.map((opt) => ({
                      value: opt.value,
                      label: opt.value,
                    })) ?? []
                  }
                  defaultLabel="選択してください"
                  value={fieldValue}
                  onValueChange={setFieldValue}
                />
              ) : selectedField?.field_type === 'multiple_select' ? (
                <MultiSelect
                  customFieldOptions={
                    selectedField.custom_field_options?.map((opt) => ({
                      id: opt.id,
                      value: opt.value,
                      label: opt.value,
                    })) || []
                  }
                  initialValues={fieldValue}
                  onChange={(selectedOptions) => {
                    const selectedValues = selectedOptions.map((option) => option.value).join(',');
                    setFieldValue(selectedValues);
                  }}
                />
              ) : (
                <TextField
                  type={selectedField?.field_type === 'number' ? 'number' : 'text'}
                  placeholder="テキスト"
                  value={fieldValue}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFieldValue(e.target.value)
                  }
                />
              )}
            </div>
          )}
        </div>

        {/* フッター */}
        <div className="flex items-center justify-end gap-4 border-t border-solid border-border px-6 py-4">
          <Button type="button" color="secondary" size="md" onClick={handleClose}>
            キャンセル
          </Button>
          <Button
            type="button"
            color="primary"
            size="md"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={!canSubmit ? 'opacity-50' : ''}
          >
            更新する
          </Button>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}
